import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_HAS_PART, RELATION_DEPENDENCY_OF, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { RiApps2Line } from '@remixicon/react';
import { TypeBadge } from '../badges/type-badge.component';
import { LifecycleBadge } from '../badges/lifecycle-badge.component';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './relations.module.css';

const HasComponentsContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2, 3].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '60px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('hasComponents.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('hasComponents.empty')} />;
  }
  return (
    <Flex direction="column" gap="3">
      {entities.map(component => {
        const ref = `${component.kind}:${component.metadata.namespace || 'default'}/${component.metadata.name}`;
        const type = (component.spec?.type as string) || undefined;
        const lifecycle = (component.spec?.lifecycle as string) || undefined;
        return (
          <Tile
            key={ref}
            href={`/catalog/${component.metadata.namespace || 'default'}/${component.kind.toLowerCase()}/${component.metadata.name}`}
            title={component.metadata.title || component.metadata.name}
            description={component.metadata.description}
            trailing={
              <>
                {type && <TypeBadge type={type} />}
                {lifecycle && <LifecycleBadge lifecycle={lifecycle} />}
              </>
            }
          />
        );
      })}
    </Flex>
  );
};

export const HasComponentsCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);

  // System → Component uses hasPart, Resource → Component uses dependencyOf
  const relationType = entity.kind === 'Resource' ? RELATION_DEPENDENCY_OF : RELATION_HAS_PART;

  const { entities, loading, error } = useRelatedEntities(entity, {
    type: relationType,
    kind: 'component',
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiApps2Line size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('hasComponents.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <HasComponentsContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
